/**
 * @module Fosc
 * @description Provides functions to interact with FOSC report-related API endpoints.
 * Utilizes axios-based BaseApiService for HTTP requests.
 */

import { apiGet, apiPost, apiPut, apiDelete, validateRequiredParams, annualReportUrls, apiPostForm } from '@/services/BaseApiService';

//#region Fosc Report
/**
 * Fetches the FOSC report for the given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch.
 * @returns {Promise<Object>} The data of the fetched report.
 * @throws {Error} If the reportId is not provided or if there is an error fetching the report.
 */
export const getFoscReport = async (reportId) => {
  validateRequiredParams({ reportId });

  return await apiGet(`${annualReportUrls.FOSC}/${reportId}`);
};

/**
 * Creates an FOSC report for the given organization, period, contact, and role.
 *
 * @param {string} organizationId - The ID of the organization.
 * @param {string} periodId - The ID of the period.
 * @param {string} contactId - The ID of the contact.
 * @param {string} roleId - The ID of the role.
 * @returns {Promise<Object>} The response data from the API.
 * @throws {Error} If any of the required parameters are missing.
 */
export const createFoscReport = async (organizationId, periodId, contactId, roleId) => {
  validateRequiredParams({ organizationId, periodId, contactId });

  return await apiPost(`${annualReportUrls.FOSC}`, { organizationId, periodId, contactId, roleId });
};

export const updateReportStatus = async(reportId, reportStatusId, isCertified) => {
  validateRequiredParams({ reportId, reportStatusId, isCertified });

  return await apiPut(`${annualReportUrls.FOSC}/${reportId}/status`, { reportId, reportStatusId, isCertified });
};

export const updateCfoReportingMonths = async(reportId, cfoName, reportingMonths) => {
  validateRequiredParams({ reportId, cfoName, reportingMonths });

  return await apiPut(`${annualReportUrls.FOSC}/${reportId}`, { reportId, cfoName, reportingMonths });
};


//#endregion Fosc Report

//#region Contacts

/**
 * Fetches the contacts for a given FOSC report.
 *
 * @param {string} reportId - The ID of the report to fetch contacts for.
 * @returns {Promise<Object>} A promise that resolves to the report contacts data.
 * @throws {Error} If the reportId is not provided or if there is an error fetching the data.
 */
export const getReportContacts = async (reportId) => {
  validateRequiredParams({ reportId });

  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/contacts`);
};

/**
 * Fetches the available report contacts for a given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch contacts for.
 * @returns {Promise<Array>} A promise that resolves to an array of available report contacts.
 * @throws {Error} If there is an error fetching the report contacts or if the response data is invalid.
 */
export const getAvailableReportContacts = async (reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/availablecontacts`);
};

/**
 * Adds a contact to a FOSC report.
 *
 * @param {string} reportId - The ID of the report to which the contact will be added.
 * @param {Object} contact - The contact information to be added to the report.
 * @throws {Error} Throws an error if the required parameters are missing.
 * @throws {Error} Throws an error if there is an issue adding the contact.
 * @returns {Promise<Object>} The response data from the API.
 */
export const addReportContact = async (reportId, contactId, roleId) => {
  validateRequiredParams({ reportId, contactId, roleId });
  return await apiPost(`${annualReportUrls.FOSC}/${reportId}/contacts`, { reportId, contactId, roleId });
};

/**
 * Adds contacts to a report.
 *
 * @param {string} reportId - The ID of the report to which contacts will be added.
 * @param {Array<{contactId: string, roleId: string}>} contacts - An array of contact objects, each containing a contactId and roleId.
 * @returns {Promise<Object>} The response data from the API.
 * @throws {Error} If required parameters are missing or if there is an error adding report contacts.
 */
export const addReportContacts = async (reportId, contacts) => {
  validateRequiredParams({ reportId, contacts });
  return await apiPost(`${annualReportUrls.FOSC}/${reportId}/contacts/bulk`, contacts);
};

/**
 * Updates the contact information for the FOSC report.
 *
 * @param {string} reportId - The ID of the report to update.
 * @param {Object} updateContact - The contact information to update.
 * @throws {Error} If required parameters are missing.
 * @throws {Error} If there is an error updating the report contact.
 * @returns {Promise<Object>} The updated report contact data.
 */
export const updateReportContact = async (reportId, updateContact) => {
  validateRequiredParams({ reportId, updateContact });
  return await apiPut(`${annualReportUrls.FOSC}/${reportId}/contacts/${updateContact.contactId}`, updateContact);
};


/**
 * Removes a contact from the FOSC report.
 *
 * @param {string} reportId - The ID of the report.
 * @param {string} contactId - The ID of the contact to be removed.
 * @throws {Error} If required parameters are missing.
 * @throws {Error} If there is an error removing the report contact.
 * @returns {Promise<Object>} The response data from the API.
 */
export const removeReportContact = async (reportId, contactId) => {
  validateRequiredParams({ reportId, contactId });
  return await apiDelete(`${annualReportUrls.FOSC}/${reportId}/contacts/${contactId}`);
};

//#endregion Contacts

//#region Facility Affiliates

/**
 * Fetches facility affiliates for a given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch facility affiliates for.
 * @returns {Promise<Object>} A promise that resolves to the data of the facility affiliates.
 * @throws {Error} Throws an error if the reportId is missing or if there is an issue with the API request.
 */
export const getFacilityAffiliates = async (reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/facilityaffiliates`);
};

/**
 * Inserts an affiliate organization for a report.
 * @param {string} reportId - The report ID.
 * @param {string} affiliateName - The name of the affiliate organization.
 * @param {string} affiliationTypeId - The affiliation type ID.
 * @returns {Promise<Object>} API response data.
 */
export const insertAffiliateOrganization = async (reportId, affiliateName, affiliationTypeId) => {
  validateRequiredParams({ reportId, affiliateName, affiliationTypeId });
  return await apiPost(`${annualReportUrls.FOSC}/${reportId}/affiliateorganization`, { reportId, affiliateName, affiliationTypeId });
};

/**
 * Gets available affiliates for FOSC reports.
 * @returns {Promise<Object>} API response data.
 */
export const getAvailableAffiliates = async () => {
  return await apiGet(`${annualReportUrls.FOSC}/availableaffiliates`);
};

/**
 * Adds or updates a facility affiliate to a report.
 *
 * @param {string} reportId - The ID of the report.
 * @param {Object} facilityAffiliate - The facility affiliate data to be added.
 * @throws {Error} Throws an error if required parameters are missing.
 * @returns {Promise<Object>} The response data from the API.
 */
export const addUpdateFacilityAffiliate = async (facilityAffiliate) => {
  validateRequiredParams({ facilityAffiliate });
  return await apiPost(`${annualReportUrls.FOSC}/${facilityAffiliate.reportId}/facilityaffiliates`, facilityAffiliate);
};

/**
 * Removes a facility affiliate from a report.
 * @param {string} reportId - The report ID.
 * @param {string} affiliateId - The affiliate ID to remove.
 * @returns {Promise<Object>} API response data.
 */
export const removeFacilityAffiliate = async (reportId, affiliateId) => {
  validateRequiredParams({ reportId, affiliateId });
  return await apiDelete(`${annualReportUrls.FOSC}/${reportId}/facilityaffiliates/${affiliateId}`);
};


//#endregion Facility Affiliates

//#region Services Procedures and Summaries

/**
 * Gets service procedures for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportServiceProcedures = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/serviceprocedure`);
};

/**
 * Updates service procedures for a report.
 * @param {Object} servicesProcedures - The procedures data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportServiceProcedures = async(servicesProcedures) => {
  validateRequiredParams({ servicesProcedures });
  return await apiPut(`${annualReportUrls.FOSC}/${servicesProcedures.reportId}/serviceprocedure`, servicesProcedures);
};

/**
 * Gets procedure summaries for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportProcedureSummary = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/proceduresummaries`);
};

/**
 * Inserts a procedure summary for a report.
 * @param {Object} procedureSummary - The summary data.
 * @returns {Promise<Object>} API response data.
 */
export const insertReportProcedureSummary = async(procedureSummary) => {
  validateRequiredParams({ procedureSummary });
  return await apiPost(`${annualReportUrls.FOSC}/${procedureSummary.reportId}/proceduresummaries`, procedureSummary);
};

/**
 * Updates a procedure summary for a report.
 * @param {Object} procedureSummary - The summary data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportProcedureSummary = async(procedureSummary) => {
  validateRequiredParams({ procedureSummary });
  console.log('Updating procedure summary:', procedureSummary);
  return await apiPut(`${annualReportUrls.FOSC}/${procedureSummary.reportId}/proceduresummaries/${procedureSummary.reportProcedureId}`, procedureSummary);
};

/**
 * Deletes a procedure summary from a report.
 * @param {string} reportId - The report ID.
 * @param {string} reportProcedureId - The procedure summary ID.
 * @returns {Promise<Object>} API response data.
 */
export const deleteReportProcedureSummary = async(reportId, reportProcedureId) => {
  validateRequiredParams({ reportId, reportProcedureId });
  return await apiDelete(`${annualReportUrls.FOSC}/${reportId}/proceduresummaries/${reportProcedureId}`);
};

//#endregion Services Procedures and Summaries

//#region Employee Classification

/**
 * Gets employee classification for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportEmployeeClassification = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/employeeclassification`);
};

/**
 * Updates employee classification for a report.
 * @param {Object} employeeClassification - The classification data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportEmployeeClassification = async(employeeClassification) => {
  validateRequiredParams({ employeeClassification });
  return await apiPut(`${annualReportUrls.FOSC}/${employeeClassification.reportId}/employeeclassification`, employeeClassification);
};

//#endregion Employee Classification

//#region Natural Expense

/**
 * Gets natural expense for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportNaturalExpense = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/naturalexpense`);
};

/**
 * Updates natural expense for a report.
 * @param {Object} naturalExpense - The expense data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportNaturalExpense = async(naturalExpense) => {
  validateRequiredParams({ naturalExpense });
  return await apiPut(`${annualReportUrls.FOSC}/${naturalExpense.reportId}/naturalexpense`, naturalExpense);
};

//#endregion Fosc Report Natural Expense

//#region Charge Adjustment

/**
 * Gets charge adjustments for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportChargeAdjustments = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/chargeadjustment`);
};

/**
 * Updates charge adjustments for a report.
 * @param {Object} chargeAdjustment - The adjustment data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportChargeAdjustments = async(chargeAdjustment) => {
  validateRequiredParams({ chargeAdjustment });
  return await apiPut(`${annualReportUrls.FOSC}/${chargeAdjustment.reportId}/chargeadjustment`, chargeAdjustment);
};

//#endregion Charge Adjustment

//#region Revenue

/**
 * Gets revenue for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportRevenue = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/revenue`);
};

/**
 * Updates revenue for a report.
 * @param {Object} revenue - The revenue data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportRevenue = async(revenue) => {
  validateRequiredParams({ revenue });
  return await apiPut(`${annualReportUrls.FOSC}/${revenue.reportId}/revenue`, revenue);
};

//#endregion Revenue

//#region Expenses

/**
 * Gets expense for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportExpense = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/expense`);
};

/**
 * Updates expense for a report.
 * @param {Object} expense - The expense data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportExpense = async(expense) => {
  validateRequiredParams({ expense });
  return await apiPut(`${annualReportUrls.FOSC}/${expense.reportId}/expense`, expense);
};

//#endregion Expenses

//#region Charity Care

/**
 * Gets charity care for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportCharityCare = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/charitycare`);
};

/**
 * Updates charity care for a report.
 * @param {Object} charityCare - The charity care data.
 * @returns {Promise<Object>} API response data.
 */
export const updateReportCharityCare = async(charityCare) => {
  validateRequiredParams({ charityCare });
  return await apiPut(`${annualReportUrls.FOSC}/${charityCare.reportId}/charitycare`, charityCare);
};

//#endregion Charity Care

//#region Documents

/**
 * Gets documents for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getReportDocuments = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/documents`);
};

/**
 * Attaches documents to a report.
 * @param {string} reportId - The report ID.
 * @param {Array<Object>} documents - Array of document objects.
 * @returns {Promise<Array>} Array of API response data.
 */
export const attachReportDocuments = async (reportId, documents) => {
  validateRequiredParams({ reportId, documents });

  // await checkToken();
  // const store = useAuthStore();
  const uploadPromises = documents.map(async (doc) => {
    if (!doc.file ) {
      throw new Error('Missing file for a document');
    }

    const transformedData =  {
      request: {
        filename: doc.fileName,
        filetype: doc.fileType,
        filesize: doc.fileSize,
        documenttypeid: doc.documentTypeId,
      },
    };
    const formData = new FormData();
    formData.append('file', doc.file);
    formData.append('request', JSON.stringify(transformedData.request));
    await apiPostForm(`${annualReportUrls.FOSC}/${reportId}/documents`, formData);
  });
  const results = await Promise.all(uploadPromises);
  return results;
};

/**
 * Removes a document from a report.
 * @param {string} reportId - The report ID.
 * @param {string} documentId - The document ID.
 * @returns {Promise<Object>} API response data.
 */
export const removeReportDocument = async (reportId, documentId) => {
  validateRequiredParams({ reportId, documentId });
  return await apiDelete(`${annualReportUrls.FOSC}/${reportId}/documents/${documentId}`);
};

//#endregion Documents

//#region Section Comments

/**
 * Gets section comments for a report.
 * @param {string} reportId - The report ID.
 * @returns {Promise<Object>} API response data.
 */
export const getSectionComments = async(reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.FOSC}/${reportId}/sectioncomments`);
};

/**
 * Updates a section comment for a report.
 * @param {string} reportId - The report ID.
 * @param {string} sectionId - The section ID.
 * @param {string} comment - The comment text.
 * @returns {Promise<Object>} API response data.
 */
export const updateSectionComment = async(reportId, sectionId, comment) => {
  validateRequiredParams({ reportId, sectionId });
  return await apiPut(`${annualReportUrls.FOSC}/${reportId}/sectioncomments/`, { reportId, sectionId, comment });
};

//#endregion Section Comments



